/**
 * PatientHeader Component
 * Displays patient information header for the report page
 */

import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { User, Phone, Mail, Calendar, MapPin, Users } from "lucide-react";
import NoImage from "../../assets/images/no-image-avatar.png";
import { DEFAULT_PROFILE_IMAGE, IMAGE_URL } from "../../app_url";

const PatientHeader = ({
  patient,
  familyMembers,
  selectedMember,
  onSelectMember,
  showFamilySelector,
}) => {
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getProfileImage = (profilePic) => {
    if (!profilePic) return DEFAULT_PROFILE_IMAGE;
    if (profilePic.startsWith("http")) return profilePic;
    return IMAGE_URL + profilePic;
  };

  const age = calculateAge(patient?.date_of_birth);

  return (
    <div className="patient-header">
      <div className="patient-header__main">
        <div className="patient-avatar">
          <img
            src={getProfileImage(patient?.profile_pic_url)}
            alt={`${patient?.first_name || "Patient"}`}
            onError={(e) => {
              e.target.src = NoImage;
            }}
          />
        </div>

        <div className="patient-details">
          <h2 className="patient-name">
            {patient?.first_name} {patient?.last_name}
          </h2>
          
          <div className="patient-meta">
            {patient?.date_of_birth && (
              <span className="meta-item">
                <Calendar size={14} />
                {moment(patient.date_of_birth).format("DD/MM/YYYY")}
                {age !== null && ` (${age} ans)`}
              </span>
            )}
            
            {patient?.gender && (
              <span className="meta-item">
                <User size={14} />
                {patient.gender}
              </span>
            )}
            
            {patient?.phone && (
              <span className="meta-item">
                <Phone size={14} />
                {patient.phone}
              </span>
            )}
            
            {patient?.email && (
              <span className="meta-item">
                <Mail size={14} />
                {patient.email}
              </span>
            )}
            
            {patient?.city && (
              <span className="meta-item">
                <MapPin size={14} />
                {patient.city}
              </span>
            )}
          </div>
        </div>
      </div>

      {showFamilySelector && familyMembers?.length > 0 && (
        <div className="patient-header__family">
          <div className="family-selector">
            <label className="family-label">
              <Users size={14} />
              Membre de la famille :
            </label>
            <select
              value={selectedMember || patient?.id || ""}
              onChange={(e) => onSelectMember(e.target.value)}
              className="family-select"
            >
              <option value={patient?.id}>
                {patient?.first_name} {patient?.last_name} (Principal)
              </option>
              {familyMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                  {member.relationship && ` (${member.relationship})`}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {patient?.medical_background?.length > 0 && (
        <div className="patient-header__background">
          <h4>Antécédents médicaux</h4>
          <div className="background-tags">
            {patient.medical_background.map((item, idx) => (
              <span key={idx} className="background-tag">
                {item.title || item.description || item.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

PatientHeader.propTypes = {
  patient: PropTypes.object,
  familyMembers: PropTypes.array,
  selectedMember: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectMember: PropTypes.func,
  showFamilySelector: PropTypes.bool,
};

PatientHeader.defaultProps = {
  patient: {},
  familyMembers: [],
  selectedMember: null,
  onSelectMember: () => {},
  showFamilySelector: true,
};

export default PatientHeader;
